import React, { ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCcw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  private handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      let errorMessage = "An unexpected error occurred within the intelligence system.";
      
      try {
        // Check if it's a Firestore JSON error
        if (this.state.error?.message) {
          const parsed = JSON.parse(this.state.error.message);
          if (parsed.error) {
            errorMessage = `Security Protocol Violation: ${parsed.error}`;
          }
        }
      } catch (e) {
        // Not a JSON error, use default or raw message
        if (this.state.error?.message) {
          errorMessage = this.state.error.message;
        }
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="glass-card max-w-md w-full p-12 rounded-[2.5rem] border-red-500/20 text-center space-y-8">
            <div className="w-20 h-20 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-500 mx-auto">
              <AlertCircle className="w-10 h-10" />
            </div>
            
            <div className="space-y-4">
              <h2 className="text-3xl font-display font-bold tracking-tight text-foreground">System <span className="text-red-500">Anomaly</span></h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {errorMessage}
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Button 
                onClick={this.handleReset}
                className="h-14 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold shadow-[0_0_30px_rgba(239,68,68,0.3)]"
              >
                <RefreshCcw className="w-5 h-5 mr-2" /> Re-establish Link
              </Button>
              <Button 
                variant="outline"
                onClick={this.handleGoHome}
                className="h-14 rounded-xl border-white/10 hover:bg-white/5"
              >
                <Home className="w-5 h-5 mr-2" /> Return to Base
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
